import { useTenant } from '@features/tenants';
import { ROUTES } from '@routes/routes';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  path: string;
}

/**
 * Custom hook for generating breadcrumb navigation from current route
 * Handles tenant name fetching for tenant detail pages
 *
 * @returns Array of breadcrumb items with labels and paths
 */
export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const location = useLocation();
  const params = useParams<{ tenantId?: string }>();
  const intl = useIntl();

  // Fetch tenant data if we have a tenantId
  const { data: tenant } = useTenant(params.tenantId || '', {
    enabled: !!params.tenantId,
  });

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItem[] = [];

    // Don't show breadcrumbs on top-level pages (Dashboard, Tenants list)
    if (location.pathname === ROUTES.DASHBOARD || location.pathname === ROUTES.TENANTS) {
      return [];
    }

    // Handle tenant routes
    if (location.pathname.startsWith('/tenants')) {
      // For detail pages, show Tenants > Detail
      items.push({
        label: intl.formatMessage({ id: 'nav.tenants' }),
        path: ROUTES.TENANTS,
      });

      // Tenant detail page - only show breadcrumb when tenant data is loaded
      if (params.tenantId && tenant?.name) {
        items.push({
          label: tenant.name,
          path: `/tenants/${params.tenantId}`,
        });

        // License creation page
        if (location.pathname.includes('/licenses/create')) {
          items.push({
            label: intl.formatMessage({ id: 'licenses.create.title' }),
            path: location.pathname,
          });
        }
      }
    }

    return items;
  }, [location.pathname, params.tenantId, intl, tenant]);

  return breadcrumbs;
};
